import React from "react";
import {
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Button,
  Upload,
  Row,
  Col,
  Typography,
} from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useEditUser } from "@/Profile/hooks/useEditUser";
import "./EditUserModal.scss";

const { Title } = Typography;

const BLOOD_GROUPS = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"];

const EditUserModal = ({ open, onClose, user, medical, onSubmit }) => {
  const { t } = useTranslation();

  const GENDER_OPTIONS = [
    { value: "male", label: t("editUser.gender.male") },
    { value: "female", label: t("editUser.gender.female") },
  ];

  const BLOOD_OPTIONS = BLOOD_GROUPS.map((g) => ({ value: g, label: g }));

  const {
    form,
    imagePreview,
    fileList,
    handleImageChange,
    handleFinish,
    handleCancel,
  } = useEditUser({ open, user, medical, onSubmit });

  return (
    <Modal
      open={open}
      onCancel={() => handleCancel(onClose)}
      footer={null}
      width={700}
      centered
      className="edit-user-modal"
      destroyOnHidden
      maskClosable={false}
    >
      <Title level={3} className="edit-title">
        {t("editUser.title")}
      </Title>

      <Form
        layout="vertical"
        form={form}
        onFinish={(values) => handleFinish(values, onClose)}
        autoComplete="off"
      >
        <Row gutter={[24, 24]}>
          {/* Avatar */}
          <Col xs={24} md={6}>
            <Form.Item label={t("editUser.fields.photo.label")}>
              <Upload
                accept="image/*"
                showUploadList={false}
                beforeUpload={() => false} // pas d’upload auto
                fileList={fileList}
                onChange={handleImageChange}
                className="avatar-upload"
                maxCount={1}
              >
                <div className="image-preview">
                  {imagePreview ? (
                    <img
                      src={imagePreview}
                      alt={t("editUser.fields.photo.alt")}
                      style={{
                        width: 100,
                        height: 100,
                        objectFit: "cover",
                        borderRadius: "50%",
                      }}
                    />
                  ) : (
                    <div className="upload-placeholder">
                      <UploadOutlined style={{ fontSize: 30, color: "#ccc" }} />
                    </div>
                  )}
                  <Button type="link" size="small">
                    {t("editUser.actions.changePhoto")}
                  </Button>
                </div>
              </Upload>
            </Form.Item>
          </Col>

          {/* Fields */}
          <Col xs={24} md={18}>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label={t("editUser.fields.firstName.label")}
                  name="firstName"
                  rules={[
                    { required: true, message: t("editUser.fields.firstName.required") },
                    { min: 2, message: t("editUser.fields.firstName.min") },
                  ]}
                >
                  <Input placeholder={t("editUser.fields.firstName.placeholder")} />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  label={t("editUser.fields.lastName.label")}
                  name="lastName"
                  rules={[
                    { required: true, message: t("editUser.fields.lastName.required") },
                    { min: 2, message: t("editUser.fields.lastName.min") },
                  ]}
                >
                  <Input placeholder={t("editUser.fields.lastName.placeholder")} />
                </Form.Item>
              </Col>

              <Col span={24}>
                <Form.Item
                  label={t("editUser.fields.email.label")}
                  name="email"
                  rules={[
                    { required: true, message: t("editUser.fields.email.required") },
                    { type: "email", message: t("editUser.fields.email.invalid") },
                  ]}
                >
                  <Input placeholder={t("editUser.fields.email.placeholder")} />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  label={t("editUser.fields.phone.label")}
                  name="phone"
                  rules={[
                    { required: true, message: t("editUser.fields.phone.required") },
                    {
                      pattern: /^[0-9+\-\s()]+$/,
                      message: t("editUser.fields.phone.invalid"),
                    },
                  ]}
                >
                  <Input placeholder={t("editUser.fields.phone.placeholder")} />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  label={t("editUser.fields.gender.label")}
                  name="gender"
                  rules={[{ required: true, message: t("editUser.fields.gender.required") }]}
                >
                  <Select
                    placeholder={t("editUser.fields.gender.placeholder")}
                    allowClear
                    options={GENDER_OPTIONS}
                  />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  label={t("editUser.fields.birthdate.label")}
                  name="birthdate"
                  rules={[
                    { required: true, message: t("editUser.fields.birthdate.required") },
                  ]}
                >
                  <DatePicker
                    style={{ width: "100%" }}
                    placeholder={t("editUser.fields.birthdate.placeholder")}
                    format="DD.MM.YYYY"
                  />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  label={t("editUser.fields.bloodGroup.label")}
                  name="groupeSanguin"
                  rules={[
                    { required: true, message: t("editUser.fields.bloodGroup.required") },
                  ]}
                >
                  <Select
                    placeholder={t("editUser.fields.bloodGroup.placeholder")}
                    allowClear
                    options={BLOOD_OPTIONS}
                  />
                </Form.Item>
              </Col>

              <Col span={24}>
                <Form.Item
                  label={t("editUser.fields.address.label")}
                  name="adresse"
                  rules={[{ required: true, message: t("editUser.fields.address.required") }]}
                >
                  <Input placeholder={t("editUser.fields.address.placeholder")} />
                </Form.Item>
              </Col>
            </Row>
          </Col>
        </Row>

        <Form.Item className="form-footer" style={{ marginBottom: 0 }}>
          <Row justify="end" gutter={12}>
            <Col>
              <Button onClick={() => handleCancel(onClose)} size="large">
                {t("editUser.actions.cancel")}
              </Button>
            </Col>
            <Col>
              <Button type="primary" htmlType="submit" size="large">
                {t("editUser.actions.save")}
              </Button>
            </Col>
          </Row>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default EditUserModal;
